/**
 * 皮肤的运行时：切换、持久化、以及**首屏不闪**。
 *
 * ⚠️ 这一层最容易做砸的是**首屏闪一下**（FOUC）：
 * 用户选的皮肤存在 localStorage 里，而 React 要等 JS 下载、解析、挂载之后才读得到它。
 * 在那之前浏览器已经用默认皮肤画过一帧了——用户每次冷启动都会看见界面从米白闪成深色。
 * PWA 尤其明显，因为它是从主屏图标全屏启动的，那一帧特别显眼。
 *
 * 唯一的解法是**在 `<head>` 里放一段同步脚本**，在首屏渲染之前就把属性写到 `<html>` 上。
 * `skinBootScript()` 生成的就是那段脚本，模板的 index.html 里已经内联好了。
 */
import { skinsToCss } from './css.js';
export const SKIN_ATTR = 'data-ps-skin';
export const THEME_ATTR = 'data-theme';
/** 默认存储键。与 boot script 必须用同一份，所以收口在这里。 */
export function skinStorageKeys(namespace) {
    return { skin: `${namespace}.skin`, mode: `${namespace}.color-mode` };
}
/* ─────────────────────────── 注入皮肤样式 ─────────────────────────── */
export const SKIN_STYLE_ID = 'pwaship-skins';
/**
 * 把自定义皮肤的 CSS 放进 `<head>`。
 *
 * ⚠️ **必须在第一次渲染之前调用**（`SkinProvider` 在 useState 初始化里调，就是为了这个）。
 * 放到 effect 里的话，React 已经用默认皮肤画过一帧了，用户会看见品牌色闪一下。
 *
 * ⚠️ 为什么不做成构建期生成一个 .css 文件：那要么引一个代码生成步骤（"改个品牌色要先跑
 * 一遍脚本"，而忘了跑的表现是**界面完全没有样式**），要么写一个在 dev 和 build 两条路
 * 上行为不同的 vite 插件。而这里根本不需要——`@pwaship/ui/styles.css` 里已经有默认皮肤，
 * 首屏是**有完整样式**的，注入只是把品牌色换上去，不存在裸奔的那一帧。
 *
 * 内容没变就不动 DOM：重复调用（StrictMode 双渲染）不会造成重排。
 */
export function ensureSkinStyles(skins, defaultName, styleId = SKIN_STYLE_ID) {
    if (typeof document === 'undefined' || skins.length === 0)
        return;
    const css = skinsToCss(skins, defaultName);
    let style = document.getElementById(styleId);
    if (style && style.textContent === css)
        return;
    if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        // 放在 head 末尾：要盖过 @pwaship/ui/styles.css 里的默认皮肤，
        // 而那份是通过 <link> 或打包器插进来的，位置在前面。
        document.head.appendChild(style);
    }
    style.textContent = css;
}
/* ─────────────────────────── 应用到 DOM ─────────────────────────── */
/**
 * 把皮肤与明暗写到 `<html>` 上。样式本身由 `ensureSkinStyles()` 负责。
 */
export function applySkin(name, mode, root) {
    const el = root ?? (typeof document === 'undefined' ? null : document.documentElement);
    if (!el)
        return;
    el.setAttribute(SKIN_ATTR, name);
    // 'system' 时要**删掉**属性而不是写 'system'：CSS 里靠 :not([data-theme="light"])
    // 判断，留一个无意义的值会让选择器行为变得难以推理。
    if (mode === 'system')
        el.removeAttribute(THEME_ATTR);
    else
        el.setAttribute(THEME_ATTR, mode);
}
/**
 * 让浏览器界面的颜色跟着皮肤走（iOS 状态栏、安卓地址栏与任务卡片）。
 *
 * ⚠️ 不做这一步的表现是"应用切成深色了，但顶上那一条还是上一套皮肤的颜色"，
 * 在 standalone 下尤其刺眼，因为那一条就贴着应用本身。
 *
 * ⚠️ **manifest 里的 `theme_color` 改不了**：它在用户"添加到主屏幕"的那一刻就被系统
 * 拷走了，之后网页再怎么改都影响不到启动画面和已安装应用的初始配色。
 * 所以 manifest 的那个值应该填**默认皮肤的**颜色，别指望运行时皮肤能改它。
 */
export function syncThemeColorMeta(color) {
    if (typeof document === 'undefined')
        return;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
    }
    meta.setAttribute('content', color);
}
/** 当前实际生效的是深色吗（把 'system' 解析成真实结果）。 */
export function resolveDark(mode) {
    if (mode === 'dark')
        return true;
    if (mode === 'light')
        return false;
    if (typeof window === 'undefined' || !window.matchMedia)
        return false;
    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    catch {
        return false;
    }
}
/** 皮肤在当前明暗下的顶栏颜色（给 theme-color 用） */
export function skinThemeColor(skin, mode) {
    return resolveDark(mode) ? skin.dark.surface : skin.light.surface;
}
/* ─────────────────────────── 首屏防闪脚本 ─────────────────────────── */
/**
 * 生成放进 `<head>` 的同步脚本。**必须内联，必须在任何样式表之后、body 之前。**
 *
 * 它做的事只有一件：把上次选的皮肤与明暗写到 `<html>` 上，赶在首屏绘制之前。
 *
 * ⚠️ 整段包在 try/catch 里：隐私模式下读 localStorage 会抛，
 * 而这段脚本一抛就是**整页白屏**（它是同步的、在 head 里）。
 * 失败方向必须是"用默认皮肤继续"，不是"什么都不显示"。
 *
 * @param namespace 与 `PwaProvider` 的 namespace 一致
 * @param fallbackSkin 没存过时用哪套
 */
export function skinBootScript(namespace, fallbackSkin = 'default') {
    const keys = skinStorageKeys(namespace);
    return (`try{var d=document.documentElement,` +
        `s=localStorage.getItem(${JSON.stringify(keys.skin)})||${JSON.stringify(fallbackSkin)},` +
        `m=localStorage.getItem(${JSON.stringify(keys.mode)});` +
        `d.setAttribute(${JSON.stringify(SKIN_ATTR)},s);` +
        `if(m==='light'||m==='dark')d.setAttribute(${JSON.stringify(THEME_ATTR)},m);` +
        `}catch(e){}`);
}
const MODES = ['system', 'light', 'dark'];
function isMode(v) {
    return typeof v === 'string' && MODES.includes(v);
}
/**
 * 读上次的选择。读不到、存的是垃圾值 —— 一律回落到默认，绝不抛。
 *
 * `known` 是当前应用真的有的皮肤名：删掉一套皮肤之后，存着旧名字的老用户
 * 必须回落到默认，否则他们会看到一个**没有任何 token 的界面**（属性挂上了但没有对应的 CSS 块），
 * 表现是全白 + 浏览器默认字体，而且刷新也不会好。
 */
export function readSkinPreference(namespace, known, fallbackSkin = 'default', storage) {
    const keys = skinStorageKeys(namespace);
    const store = storage === undefined ? safeLocalStorage() : storage;
    const read = (k) => {
        try {
            return store?.getItem(k) ?? null;
        }
        catch {
            return null;
        }
    };
    const savedSkin = read(keys.skin);
    const savedMode = read(keys.mode);
    return {
        skin: savedSkin && known.includes(savedSkin) ? savedSkin : fallbackSkin,
        mode: isMode(savedMode) ? savedMode : 'system',
    };
}
export function writeSkinPreference(namespace, pref, storage) {
    const keys = skinStorageKeys(namespace);
    const store = storage === undefined ? safeLocalStorage() : storage;
    try {
        if (pref.skin !== undefined)
            store?.setItem(keys.skin, pref.skin);
        if (pref.mode !== undefined)
            store?.setItem(keys.mode, pref.mode);
    }
    catch {
        // 隐私模式：这次选择活不过刷新。比把人挡在外面好。
    }
}
function safeLocalStorage() {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=runtime.js.map